import React, { useState, useEffect } from 'react';
import RichTextEditor from '../Common/RichTextEditor';
import NoticeList from '../Common/NoticeList';
import NoticeCard from '../Common/NoticeCard';
import { Notice, Building, NoticeFormData, NoticeAttachment } from '../../types/Notice';
import api from '../../services/api';

const NoticeManager: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [showNoticeDetail, setShowNoticeDetail] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'edit' | 'detail' | 'statistics'>('list');
  const [attachments, setAttachments] = useState<NoticeAttachment[]>([]);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  
  const [noticeForm, setNoticeForm] = useState<NoticeFormData>({
    title: '',
    content: '',
    excerpt: '',
    type: 'central',
    building_id: null,
    priority: 'medium',
    status: 'draft',
    is_urgent: false,
    scheduled_at: '',
    expires_at: '',
    send_notification: true,
    target_audience: ['all'],
    attachment: null
  });

  useEffect(() => {
    fetchNotices();
    fetchBuildings();
    fetchStatistics();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notices');
      if (response.data.success) {
        setNotices(response.data.data.data || response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/buildings');
      setBuildings(response.data);
    } catch (error: any) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/notices/statistics');
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare the data with proper type conversion
      const submitData: any = {
        title: noticeForm.title,
        content: noticeForm.content,
        excerpt: noticeForm.excerpt || null,
        type: noticeForm.type,
        building_id: noticeForm.building_id,
        priority: noticeForm.priority,
        status: noticeForm.status,
        is_urgent: Boolean(noticeForm.is_urgent),
        scheduled_at: noticeForm.scheduled_at || null,
        expires_at: noticeForm.expires_at || null,
        send_notification: Boolean(noticeForm.send_notification),
        target_audience: noticeForm.target_audience
      };

        // Handle file upload if present
        if (noticeForm.attachment) {
          const formData = new FormData();
          Object.entries(submitData).forEach(([key, value]) => {
            if (value === null || value === '' || value === undefined) return;
            if (key === 'target_audience' && Array.isArray(value)) {
              value.forEach((v: string) => formData.append('target_audience[]', v));
            } else if (key === 'is_urgent' || key === 'send_notification') {
              formData.append(key, (value ? '1' : '0'));
            } else {
              formData.append(key, value.toString());
            }
          });
          formData.append('attachment', noticeForm.attachment);

        if (editingNotice) {
          await api.put(`/notices/${editingNotice.id}`, formData);
        } else {
          await api.post('/notices', formData);
        }
      } else {
        // No file upload, send as JSON
        if (editingNotice) {
          await api.put(`/notices/${editingNotice.id}`, submitData);
        } else {
          await api.post('/notices', submitData);
        }
      }

      resetForm();
      fetchNotices();
      fetchStatistics();
      setActiveTab('list');
    } catch (error: any) {
      console.error('Error saving notice:', error);
      alert(error.response?.data?.message || 'Error saving notice');
    }
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    loadAttachments(notice.id);
    setNoticeForm({
      title: notice.title,
      content: notice.content,
      excerpt: notice.excerpt || '',
      type: notice.type,
      building_id: notice.building_id || null,
      priority: notice.priority,
      status: notice.status,
      is_urgent: notice.is_urgent,
      scheduled_at: notice.scheduled_at ? notice.scheduled_at.split('T')[0] : '',
      expires_at: notice.expires_at ? notice.expires_at.split('T')[0] : '',
      send_notification: true,
      target_audience: notice.target_audience || ['all'],
      attachment: null
    });
    setActiveTab('edit');
  };

  const loadAttachments = async (noticeId: number) => {
    try {
      const res = await api.get(`/notices/${noticeId}/attachments`);
      if (res.data?.success) {
        setAttachments(res.data.data || []);
      }
    } catch (e) {
      console.error('Error loading attachments', e);
    }
  };

  const handleDelete = async (notice: Notice) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      try {
        await api.delete(`/notices/${notice.id}`);
        fetchNotices();
        fetchStatistics();
      } catch (error: any) {
        console.error('Error deleting notice:', error);
        alert(error.response?.data?.message || 'Error deleting notice');
      }
    }
  };

  const handleArchive = async (notice: Notice) => {
    try {
      await api.post(`/notices/${notice.id}/archive`);
      fetchNotices();
      fetchStatistics();
    } catch (error: any) {
      console.error('Error archiving notice:', error);
      alert(error.response?.data?.message || 'Error archiving notice');
    }
  };

  const handlePublish = async (notice: Notice) => {
    try {
      await api.post(`/notices/${notice.id}/publish`);
      fetchNotices();
      fetchStatistics();
    } catch (error: any) {
      console.error('Error publishing notice:', error);
      alert(error.response?.data?.message || 'Error publishing notice');
    }
  };

  const handleViewDetail = (notice: Notice) => {
    setSelectedNotice(notice);
    setShowNoticeDetail(true);
    setActiveTab('detail');
  };

  const resetForm = () => {
    setNoticeForm({
      title: '',
      content: '',
      excerpt: '',
      type: 'central',
      building_id: null,
      priority: 'medium',
      status: 'draft',
      is_urgent: false,
      scheduled_at: '',
      expires_at: '',
      send_notification: true,
      target_audience: ['all'],
      attachment: null
    });
    setEditingNotice(null);
    setShowForm(false);
    setAttachments([]);
    setNewAttachments([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNoticeForm({ ...noticeForm, attachment: file });
  };

  const handleNewAttachmentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewAttachments(files);
  };

  const handleUploadNewAttachments = async () => {
    if (!editingNotice || newAttachments.length === 0) return;
    try {
      const form = new FormData();
      newAttachments.forEach(f => form.append('attachments[]', f));
      const res = await api.post(`/notices/${editingNotice.id}/attachments`, form);
      if (res.data?.success) {
        await loadAttachments(editingNotice.id);
        setNewAttachments([]);
        // reset file input
        const input = document.getElementById('notice-new-attachments') as HTMLInputElement | null;
        if (input) input.value = '';
      }
    } catch (e: any) {
      console.error('Upload failed', e);
      alert(e.response?.data?.message || 'Error uploading attachments');
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!editingNotice) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.delete(`/notices/${editingNotice.id}/attachments/${attachmentId}`);
      await loadAttachments(editingNotice.id);
    } catch (e: any) {
      console.error('Delete failed', e);
      alert(e.response?.data?.message || 'Error deleting attachment');
    }
  };

  const handleTargetAudienceChange = (value: string) => {
    const current = noticeForm.target_audience;
    if (current.includes(value)) {
      setNoticeForm({
        ...noticeForm,
        target_audience: current.filter(item => item !== value)
      });
    } else {
      setNoticeForm({
        ...noticeForm,
        target_audience: [...current, value]
      });
    }
  };

  if (loading) {
    return (
      <div className="notice-manager-loading">
        <div className="loading-spinner"></div>
        <p>Loading notices...</p>
      </div>
    );
  }

  return (
    <div className="notice-manager">
      {/* Header */}
      <div className="notice-manager-header">
        <h1>Notice Management</h1>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setActiveTab('create');
            }}
          >
            + Create Notice
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setActiveTab('statistics')}
          >
            📊 Statistics
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="notice-tabs">
        <button
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          📋 All Notices
        </button>
        <button
          className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => {
            resetForm();
            setActiveTab('create');
          }}
        >
          ➕ Create New
        </button>
        {editingNotice && (
          <button
            className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            ✏️ Edit Notice
          </button>
        )}
        {selectedNotice && (
          <button
            className={`tab-btn ${activeTab === 'detail' ? 'active' : ''}`}
            onClick={() => setActiveTab('detail')}
          >
            👁️ View Detail
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          📊 Statistics
        </button>
      </div>

      {/* Tab Content */}
      <div className="notice-tab-content">
        {/* List Tab */}
        {activeTab === 'list' && (
          <NoticeList
            showActions={true}
            adminMode={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onPublish={handlePublish}
          />
        )}

        {/* Create/Edit Tab */}
        {(activeTab === 'create' || activeTab === 'edit') && (
          <div className="notice-form-container">
            <div className="form-header">
              <h2>{editingNotice ? 'Edit Notice' : 'Create New Notice'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="notice-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={noticeForm.title}
                    onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                    required
                    placeholder="Enter notice title"
                  />
                </div>

                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={noticeForm.type}
                    onChange={(e) => setNoticeForm({ ...noticeForm, type: e.target.value as 'central' | 'building' })}
                    required
                  >
                    <option value="central">Central Committee</option>
                    <option value="building">Building Committee</option>
                  </select>
                </div>
              </div>

              {noticeForm.type === 'building' && (
                <div className="form-group">
                  <label>Building *</label>
                  <select
                    value={noticeForm.building_id || ''}
                    onChange={(e) => setNoticeForm({ ...noticeForm, building_id: parseInt(e.target.value) || null })}
                    required
                  >
                    <option value="">Select Building</option>
                    {buildings.map(building => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={noticeForm.priority}
                    onChange={(e) => setNoticeForm({ ...noticeForm, priority: e.target.value as any })}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={noticeForm.status}
                    onChange={(e) => setNoticeForm({ ...noticeForm, status: e.target.value as any })}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Excerpt</label>
                <textarea
                  value={noticeForm.excerpt}
                  onChange={(e) => setNoticeForm({ ...noticeForm, excerpt: e.target.value })}
                  placeholder="Brief description of the notice (optional)"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={noticeForm.content}
                  onChange={(content) => setNoticeForm({ ...noticeForm, content })}
                  placeholder="Enter notice content here..."
                  height="300px"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Scheduled Date</label>
                  <input
                    type="datetime-local"
                    value={noticeForm.scheduled_at}
                    onChange={(e) => setNoticeForm({ ...noticeForm, scheduled_at: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Expires Date</label>
                  <input
                    type="datetime-local"
                    value={noticeForm.expires_at}
                    onChange={(e) => setNoticeForm({ ...noticeForm, expires_at: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Target Audience</label>
                <div className="checkbox-group">
                  {['all', 'building_owners', 'committee_members', 'specific_buildings'].map(audience => (
                    <label key={audience} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={noticeForm.target_audience.includes(audience)}
                        onChange={() => handleTargetAudienceChange(audience)}
                      />
                      {audience.replace('_', ' ').toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Attachment</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <small>Optional single attachment for create/update</small>
              </div>

              {editingNotice && (
                <div className="form-group">
                  <label>Existing Attachments</label>
                  {attachments.length === 0 ? (
                    <div className="empty-state">No attachments</div>
                  ) : (
                    <ul className="attachment-list">
                      {attachments.map(att => (
                        <li key={att.id} className="attachment-item">
                          <span className="name">{att.file_name}</span>
                          <a href={att.url} target="_blank" rel="noreferrer" className="btn btn-link">View</a>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteAttachment(att.id)}>Delete</button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="attachment-upload">
                    <label>Add More Attachments</label>
                    <input
                      id="notice-new-attachments"
                      type="file"
                      multiple
                      onChange={handleNewAttachmentsChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                    />
                    {newAttachments.length > 0 && (
                      <div className="selected-files">
                        {newAttachments.map((f, idx) => (
                          <span key={idx} className="file-chip">{f.name}</span>
                        ))}
                      </div>
                    )}
                    <button type="button" className="btn btn-outline-primary" onClick={handleUploadNewAttachments} disabled={newAttachments.length === 0}>
                      Upload Selected
                    </button>
                    <small>Supported: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, ZIP (Max 10MB each)</small>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={noticeForm.is_urgent}
                    onChange={(e) => setNoticeForm({ ...noticeForm, is_urgent: e.target.checked })}
                  />
                  Mark as Urgent
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={noticeForm.send_notification}
                    onChange={(e) => setNoticeForm({ ...noticeForm, send_notification: e.target.checked })}
                  />
                  Send Notification to Target Audience
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingNotice ? 'Update Notice' : 'Create Notice'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    resetForm();
                    setActiveTab('list');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Detail Tab */}
        {activeTab === 'detail' && selectedNotice && (
          <div className="notice-detail-container">
            <div className="detail-header">
              <h2>Notice Details</h2>
              <div className="detail-actions">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => handleEdit(selectedNotice)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setActiveTab('list')}
                >
                  Back to List
                </button>
              </div>
            </div>
            <NoticeCard
              notice={selectedNotice}
              showFullContent={true}
              showActions={true}
              onEdit={() => handleEdit(selectedNotice)}
              onDelete={() => handleDelete(selectedNotice)}
              onArchive={() => handleArchive(selectedNotice)}
              onPublish={() => handlePublish(selectedNotice)}
            />
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'statistics' && statistics && (
          <div className="notice-statistics">
            <h2>Notice Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Notices</h3>
                <div className="stat-number">{statistics.total}</div>
              </div>
              <div className="stat-card">
                <h3>Published</h3>
                <div className="stat-number">{statistics.published}</div>
              </div>
              <div className="stat-card">
                <h3>Draft</h3>
                <div className="stat-number">{statistics.draft}</div>
              </div>
              <div className="stat-card">
                <h3>Archived</h3>
                <div className="stat-number">{statistics.archived}</div>
              </div>
              <div className="stat-card">
                <h3>Urgent</h3>
                <div className="stat-number">{statistics.urgent}</div>
              </div>
              <div className="stat-card">
                <h3>Central</h3>
                <div className="stat-number">{statistics.central}</div>
              </div>
              <div className="stat-card">
                <h3>Building</h3>
                <div className="stat-number">{statistics.building}</div>
              </div>
              <div className="stat-card">
                <h3>Total Views</h3>
                <div className="stat-number">{statistics.recent_views}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeManager;